import { DEFAULT_BRAND, IS_PRIVATE_BUILD, NEUTRAL_BRAND, NEUTRAL_MODULES } from "./defaultBrand";
import { applyDocumentBrand } from "./document";
import { isListedDirectHost, isPartnerSubzoneHost, normalizeHost } from "./hosts";
import { getBrand, setBrand, setPartnerNotFound } from "./store";
import { applyBrandOverlay } from "./themeOverlay";
import { readImpersonationBrand } from "./impersonationBrand";
import type { Brand, BrandEntry, BrandModules } from "./types";

/**
 * 品牌启动(设计稿 §1「启动流程」)。6 个 main 在 createRoot 之前 `await bootstrapBrand(entry)`。
 *
 * 取品牌的顺序:
 *   1. localStorage `gscq-brand:v1:<host>` 有缓存 → 立即套用,后台 SWR 刷新(刷到不同的才再套一次);
 *   2. 无缓存 + 渠道域名 → 等 `GET /api/v1/public/brand`,首屏最多 1500ms;超时先回落,
 *      请求不 abort、继续等到 15s,晚到的回包照 SWR 口径套用 + 写缓存(PARTNER_NOT_FOUND 同样生效);
 *   3. 拉取失败(接口还没上 404 / 超时 / 网络错 / 回包不认识)→ 按域名回落:
 *        直营白名单 → DEFAULT_BRAND;白名单外 → NEUTRAL_BRAND(宁可中性,绝不闪「销帮」)。
 * ★直营白名单域名无缓存时**不等**接口:回落值本来就是 DEFAULT,等也只是白白压首屏;
 *   后台照样拉一次写缓存,平台改了 system 品牌下次打开即生效。
 * ★这个请求不带 token、不走 httpClient:公开接口,不许触发 401 → 跳登录那条链。
 *
 * OEM W5 私有化:
 *   · 构建开关 VITE_PRIVATE=1 → 直营白名单清空(私有化现场多是裸 IP 访问,原先会直接闪 DEFAULT),
 *     无缓存一律等接口,失败回落 NEUTRAL;
 *   · SaaS 构建但回包带 `neutralBase`(= 旧镜像装进了私有化实例)→ 清掉本机所有直营品牌缓存,
 *     并记一个标记:此后直营白名单域名也不再「不等接口直接 DEFAULT」,下次首屏不闪「销帮」。
 *
 * OEM D1 域名方案(7.2):
 *   · 直营白名单改显式列表(见 hosts.ts),渠道子区 `*.oem.xbwhynot.com` / `*.oem-demo.xbwhynot.com`
 *     一律不算直营;子区下缓存里残留的直营品牌(旧版通配时写入的)当没缓存并清掉;
 *   · 接口回 404 且 code=PARTNER_NOT_FOUND(子区下 code 不存在 / 渠道停用)→ 清该 Host 的品牌缓存,
 *     置 partnerNotFound,入口只渲染中性的「渠道不存在」页(PartnerNotFoundGate),**不回落直营**。
 *     不带该 code 的 404(老后端没这个接口)仍按拉取失败处理。
 */

export const BRAND_ENDPOINT = "/api/v1/public/brand";
export const BRAND_TIMEOUT_MS = 1500;
/** 首屏超时回落后,同一个请求继续等的上限(弱网晚到的回包仍套用 + 写缓存)。 */
export const BRAND_LATE_TIMEOUT_MS = 15000;
export const BRAND_CACHE_PREFIX = "gscq-brand:v1:";
/** 「本实例是 neutralBase」标记(见文件头 OEM W5 段)。不是合法 hostname,不会与品牌缓存键撞。 */
export const NEUTRAL_BASE_MARK_KEY = `${BRAND_CACHE_PREFIX}@neutral-base`;

/** 后端「渠道不存在」错误码(子区下 code 不存在或渠道未启用,HTTP 404)。 */
export const PARTNER_NOT_FOUND_CODE = "PARTNER_NOT_FOUND";

/** 直营白名单:这些域名无缓存时不等接口直接 DEFAULT,拉取失败也回落 DEFAULT。 */
export function isDirectHost(hostname: string): boolean {
  // 私有化构建:没有「直营」可言,白名单清空。
  if (IS_PRIVATE_BUILD) return false;
  const h = normalizeHost(hostname);
  if (!h) return true;
  // 渠道子区:一律等 /public/brand(子区下后端只回渠道品牌或 PARTNER_NOT_FOUND)。
  if (isPartnerSubzoneHost(h)) return false;
  if (isListedDirectHost(h)) return true;
  // 存量直营租户的企微 H5 域名 gscq.<客户域>(nginx 同样按 ^gscq\. 落 wecom-h5)。
  // 渠道客户按 7.2 用 h5.<客户域>,不走这条。
  if (h.startsWith("gscq.")) return true;
  return false;
}

/**
 * 当前品牌是不是「后端认定的渠道」(= Host 解析到了非 system 渠道)。
 * DEFAULT(直营 / 未知域名按 system)与 NEUTRAL(渠道域名但品牌没拉到)都不算 —— NEUTRAL 时拿不准,
 * 登录页按「可能是渠道」处理(见 LoginPage),但登录端点不做排他。
 */
export function isPartnerBrand(brand: Pick<Brand, "key">): boolean {
  return brand.key !== DEFAULT_BRAND.key && brand.key !== NEUTRAL_BRAND.key;
}

/**
 * 1004 O5:「更新记录」(发版号 + 发版说明)只给直营看 —— 那是我方的版本节奏和发版叙事,渠道租户 / 中性兜底品牌一律不露
 * (侧栏帮助组、帮助页按钮、自动弹的「本次更新」、/help/changelog 路由四处同一个判据)。
 */
export function showsReleaseNotes(brand: Pick<Brand, "key">): boolean {
  return brand.key === DEFAULT_BRAND.key;
}

export function fallbackBrandFor(hostname: string): Brand {
  return isDirectHost(hostname) ? DEFAULT_BRAND : NEUTRAL_BRAND;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

/** 协议外链只认 http(s) 绝对地址(挡 `javascript:` 之类);不合格 = 当没配,走站内默认页。 */
function safeHttpUrl(v: unknown): string | undefined {
  const s = str(v);
  return s && /^https?:\/\//i.test(s) ? s : undefined;
}

function bool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

/**
 * 把接口回包映射成 Brand。
 * 认两种形状:① 后端 PartnerBrand(displayName / themeColor / icpNo / legalEntityName …,
 * 设计-api-渠道层与品牌.md §1;契约源 = gscq-api partner/dto/PublicBrandView);② 前端 Brand 同名字段。
 * 字段对照(W3 C-F 对齐真实契约):displayName→name、productName→productName、brainName→shortName、
 * advisorName→persona.title、assistantName→persona.name、orgName/shellName/subtitle 同名、
 * logoDarkUrl、themeColorSecondary→secondaryColor、icpNo→icp、policeNo、termsUrl/privacyUrl、
 * advisorGreeting、loginSlogan;顶层 modules → BrandModules,primaryHost → loginOrigin。
 * ★渠道回包里后端只继承了 system 的通用字段(人设名/开场白/副标题),其余缺的由 NEUTRAL 补。
 * 外层可包 `{success, data}` 信封,data 里可以是 `{partnerCode, brand, primaryHost, modules?}`。
 * 认不出来返回 null(= 当作拉取失败走回落)。
 */
export function normalizeBrandPayload(payload: unknown): Brand | null {
  if (!payload || typeof payload !== "object") return null;
  let data = payload as Record<string, unknown>;
  if ("success" in data) {
    if (data.success !== true) return null;
    data = (data.data ?? null) as Record<string, unknown>;
    if (!data || typeof data !== "object") return null;
  }
  const b = (data.brand && typeof data.brand === "object" ? data.brand : data) as Record<
    string,
    unknown
  >;

  const partnerCode = str(data.partnerCode) ?? str(b.key) ?? str(b.partnerCode);
  const displayName = str(b.displayName) ?? str(b.name);
  if (!partnerCode && !displayName) return null;

  // system 渠道 = 直营:在 DEFAULT 之上覆写(titles / 英文名 / 模块全开都沿用)。
  const isSystem = partnerCode === "system" || partnerCode === "default";
  // OEM W5 私有化:后端 private-mode 回 `neutralBase: true` —— 实例里的 system 不是「销帮直营」,
  // 缺的字段一律从 NEUTRAL 兜底(不带 DEFAULT 的「销帮」字样 / 我方主体 / 逐字标题)。
  // 身份(key / partnerId)仍按 isSystem 走:私有化实例的登录口径与直营相同。不带该字段 → 与改造前逐字相同。
  const neutralBase = data.neutralBase === true || b.neutralBase === true;
  // 私有化构建里 DEFAULT 本身就是中性口径;统一按 neutralBase 走,模块也按回包 / NEUTRAL 取。
  const directBase = isSystem && !neutralBase && !IS_PRIVATE_BUILD;
  const base = directBase ? DEFAULT_BRAND : NEUTRAL_BRAND;
  const productName = str(b.productName) ?? displayName ?? base.productName;
  const name = displayName ?? base.name;

  const rawModules = (data.modules ?? b.modules) as Record<string, unknown> | undefined;
  const modules: BrandModules = { ...(directBase ? DEFAULT_BRAND.modules : NEUTRAL_MODULES) };
  if (rawModules && typeof rawModules === "object") {
    for (const k of Object.keys(modules) as (keyof BrandModules)[]) {
      const v = bool(rawModules[k]);
      if (v !== undefined) modules[k] = v;
    }
  }

  const legalRaw = (b.legal ?? {}) as Record<string, unknown>;
  const supportRaw = (b.support ?? {}) as Record<string, unknown>;
  const personaRaw = (b.persona ?? {}) as Record<string, unknown>;
  const primaryHost = str(data.primaryHost);

  const brand: Brand = {
    ...base,
    key: isSystem ? DEFAULT_BRAND.key : (partnerCode ?? "partner"),
    partnerId: str(b.partnerId) ?? (isSystem ? null : (partnerCode ?? null)),
    name,
    subtitle: str(b.subtitle) ?? base.subtitle,
    productName,
    // 后端 PartnerBrand.brainName(人设「智脑」)= 登录卡「登录{shortName}」;渠道没配时后端已从 system 继承。
    shortName: str(b.shortName) ?? str(b.brainName) ?? (directBase ? base.shortName : productName),
    shellName: str(b.shellName) ?? (directBase ? base.shellName : productName),
    orgName: str(b.orgName) ?? (directBase ? base.orgName : ""),
    copyright: str(b.copyright) ?? (directBase ? base.copyright : ""),
    description: str(b.description) ?? (directBase ? base.description : productName),
    logoUrl: str(b.logoUrl) ?? base.logoUrl,
    logoDarkUrl: str(b.logoDarkUrl) ?? base.logoDarkUrl,
    faviconUrl: str(b.faviconUrl) ?? base.faviconUrl,
    primaryColor: str(b.themeColor) ?? str(b.primaryColor) ?? base.primaryColor,
    secondaryColor: str(b.themeColorSecondary) ?? str(b.secondaryColor) ?? base.secondaryColor,
    icp: str(b.icpNo) ?? str(b.icp) ?? base.icp,
    policeNo: str(b.policeNo) ?? base.policeNo,
    termsUrl: safeHttpUrl(b.termsUrl) ?? base.termsUrl,
    privacyUrl: safeHttpUrl(b.privacyUrl) ?? base.privacyUrl,
    legal: {
      company: str(b.legalEntityName) ?? str(legalRaw.company) ?? base.legal.company,
      address: str(b.legalEntityAddress) ?? str(legalRaw.address) ?? base.legal.address,
      product: str(legalRaw.product) ?? (directBase ? base.legal.product : productName),
    },
    support: {
      text: str(b.supportText) ?? str(supportRaw.text) ?? base.support.text,
      phone: str(b.supportPhone) ?? str(supportRaw.phone) ?? base.support.phone,
      qrUrl: str(b.supportQrUrl) ?? str(supportRaw.qrUrl) ?? base.support.qrUrl,
    },
    persona: {
      title: str(b.advisorName) ?? str(personaRaw.title) ?? base.persona.title,
      name: str(b.assistantName) ?? str(personaRaw.name) ?? base.persona.name,
    },
    advisorGreeting: str(b.advisorGreeting) ?? base.advisorGreeting,
    loginSlogan: str(b.loginSlogan) ?? base.loginSlogan,
    modules,
    loginOrigin:
      str(b.loginOrigin) ??
      (primaryHost
        ? primaryHost.includes("://")
          ? primaryHost
          : `https://${primaryHost}`
        : base.loginOrigin),
  };
  // 渠道品牌:DEFAULT 的逐字标题 / 英文名一律不带过去(那是直营字样)。
  if (!directBase) {
    delete brand.titles;
    delete brand.locales;
  }
  // 只标在 system 上(= 本实例自身是 neutralBase);渠道回包本来就以 NEUTRAL 为底,带不带结果逐字相同。
  if (neutralBase && isSystem) brand.neutralBase = true;
  else delete brand.neutralBase;
  return brand;
}

/** 清掉某个 Host 的品牌缓存(渠道不存在时用)。 */
export function clearBrandCache(hostname: string, storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(BRAND_CACHE_PREFIX + hostname);
  } catch {
    // 隐私模式等:忽略
  }
}

/** 缓存读写(localStorage 不可用时静默)。 */
export function readBrandCache(hostname: string, storage: Storage | null = safeStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BRAND_CACHE_PREFIX + hostname);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { brand?: Brand };
    const b = parsed?.brand;
    if (!b || typeof b.name !== "string" || typeof b.key !== "string" || !b.modules) return null;
    return b;
  } catch {
    return null;
  }
}

export function writeBrandCache(
  hostname: string,
  brand: Brand,
  storage: Storage | null = safeStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(BRAND_CACHE_PREFIX + hostname, JSON.stringify({ savedAt: Date.now(), brand }));
  } catch {
    // 配额满 / 隐私模式:下次再拉
  }
}

/** 缓存里的是不是「直营品牌」(system 且非 neutralBase):neutralBase 实例上要清掉的就是它。 */
function isDirectCached(b: Brand): boolean {
  return b.key === DEFAULT_BRAND.key && b.neutralBase !== true;
}

/**
 * 回包带 neutralBase:清掉本机所有域名下缓存的直营品牌 + 记标记;不带:撤标记。
 * 仅 SaaS 构建有意义(私有化构建的 DEFAULT 本来就是中性的),但两种构建都跑,无副作用。
 */
export function syncNeutralBaseCache(fresh: Brand, storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    if (fresh.neutralBase !== true) {
      storage.removeItem(NEUTRAL_BASE_MARK_KEY);
      return;
    }
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (!k || !k.startsWith(BRAND_CACHE_PREFIX) || k === NEUTRAL_BASE_MARK_KEY) continue;
      const cached = readBrandCache(k.slice(BRAND_CACHE_PREFIX.length), storage);
      if (!cached || isDirectCached(cached)) doomed.push(k);
    }
    doomed.forEach((k) => storage.removeItem(k));
    storage.setItem(NEUTRAL_BASE_MARK_KEY, "1");
  } catch {
    // 隐私模式等:下次再清
  }
}

function hasNeutralBaseMark(storage: Storage | null): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(NEUTRAL_BASE_MARK_KEY) === "1";
  } catch {
    return false;
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** 拉品牌:超时/非 2xx/网络错/回包不认识 一律返回 null。不带 Authorization。 */
export async function fetchBrand(
  opts: { fetchImpl?: typeof fetch; timeoutMs?: number } = {}
): Promise<Brand | null> {
  const r = await fetchBrandResult(opts);
  return r.kind === "ok" ? r.brand : null;
}

/** 拉取结果:拿到品牌 / 渠道不存在(404 PARTNER_NOT_FOUND)/ 其余失败(超时、网络错、老后端 404 …)。 */
export type BrandFetchResult =
  | { kind: "ok"; brand: Brand }
  | { kind: "not-found" }
  | { kind: "fail" };

const FAIL: BrandFetchResult = { kind: "fail" };

async function isPartnerNotFoundResponse(res: Response): Promise<boolean> {
  if (res.status !== 404) return false;
  try {
    const body = (await res.json()) as Record<string, unknown> | null;
    if (!body || typeof body !== "object") return false;
    const data = body.data as Record<string, unknown> | null | undefined;
    return (
      body.code === PARTNER_NOT_FOUND_CODE ||
      (!!data && typeof data === "object" && data.code === PARTNER_NOT_FOUND_CODE)
    );
  } catch {
    return false;
  }
}

export async function fetchBrandResult(
  opts: { fetchImpl?: typeof fetch; timeoutMs?: number } = {}
): Promise<BrandFetchResult> {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
  if (!fetchImpl) return FAIL;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<BrandFetchResult>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(FAIL);
    }, opts.timeoutMs ?? BRAND_TIMEOUT_MS);
  });
  const request = (async (): Promise<BrandFetchResult> => {
    try {
      const res = await fetchImpl(BRAND_ENDPOINT, {
        method: "GET",
        credentials: "omit",
        headers: { Accept: "application/json" },
        signal: controller?.signal,
      });
      if (!res.ok) {
        return (await isPartnerNotFoundResponse(res)) ? { kind: "not-found" } : FAIL;
      }
      const brand = normalizeBrandPayload(await res.json());
      return brand ? { kind: "ok", brand } : FAIL;
    } catch {
      return FAIL;
    }
  })();
  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function sameBrand(a: Brand, b: Brand): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export interface BootstrapOptions {
  hostname?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  storage?: Storage | null;
  doc?: Document;
  /** SWR 后台刷新完成回调(测试用)。 */
  onRefreshed?: (brand: Brand | null) => void;
}

function applyBrand(entry: BrandEntry, brand: Brand, doc?: Document) {
  setBrand(brand);
  const d = doc ?? (typeof document !== "undefined" ? document : undefined);
  if (d) {
    applyDocumentBrand(entry, brand, d);
    applyBrandOverlay(brand, d.documentElement);
  }
}

export async function bootstrapBrand(
  entry: BrandEntry,
  opts: BootstrapOptions = {}
): Promise<Brand> {
  const hostname = (
    opts.hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
  const storage = opts.storage === undefined ? safeStorage() : opts.storage;
  // 标记过 neutralBase 的实例:直营白名单域名也按中性处理(等接口,失败回落 NEUTRAL)。
  const neutralMarked = hasNeutralBaseMark(storage);
  const fallback = neutralMarked ? NEUTRAL_BRAND : fallbackBrandFor(hostname);

  const remember = (fresh: Brand) => {
    syncNeutralBaseCache(fresh, storage);
    writeBrandCache(hostname, fresh, storage);
  };

  // 渠道不存在:清缓存 + 置标记(入口的 PartnerNotFoundGate 据此只渲染中性提示页),品牌落 NEUTRAL。
  const markNotFound = () => {
    clearBrandCache(hostname, storage);
    applyBrand(entry, NEUTRAL_BRAND, opts.doc);
    setPartnerNotFound(true);
  };

  const refresh = () =>
    fetchBrandResult(opts).then((r) => {
      const fresh = r.kind === "ok" ? r.brand : null;
      if (fresh) {
        remember(fresh);
        if (!sameBrand(fresh, getBrand())) applyBrand(entry, fresh, opts.doc);
      } else if (r.kind === "not-found") {
        markNotFound();
      }
      opts.onRefreshed?.(fresh);
      return fresh;
    });

  // 1004 F1b:代客中 → 套被代客租户所属渠道的品牌(进入代客时用代客 token 拉的 /me/brand 存档),
  // 平台人员看到的就是客户看到的;不刷新 Host 品牌缓存(那是本机按域名的品牌,退出代客后照用)。
  const impersonated = readImpersonationBrand(storage);
  if (impersonated) {
    applyBrand(entry, impersonated, opts.doc);
    return impersonated;
  }

  const subzone = isPartnerSubzoneHost(hostname);
  const cachedRaw = readBrandCache(hostname, storage);
  // 已知是 neutralBase 实例却读到直营缓存(清理前写入的):当没缓存,别拿它闪首屏。
  // 渠道子区下的直营缓存 = 旧版 `*.xbwhynot.com` 通配时写入的毒缓存:清掉,等接口。
  let cached = cachedRaw && neutralMarked && isDirectCached(cachedRaw) ? null : cachedRaw;
  if (cached && subzone && cached.key === DEFAULT_BRAND.key) {
    clearBrandCache(hostname, storage);
    cached = null;
  }
  if (cached) {
    applyBrand(entry, cached, opts.doc);
    void refresh();
    return cached;
  }

  if (fallback === DEFAULT_BRAND) {
    applyBrand(entry, DEFAULT_BRAND, opts.doc);
    void refresh();
    return DEFAULT_BRAND;
  }

  // 首屏最多等 timeoutMs(默认 1500ms)就回落;真实请求**不 abort**,放宽到 BRAND_LATE_TIMEOUT_MS
  // 继续等 —— 弱网(TLS 就要 2s)晚到的回包照样套用并写缓存,不至于整个会话中性、每次打开重赌。
  const firstPaintMs = opts.timeoutMs ?? BRAND_TIMEOUT_MS;
  const pending = fetchBrandResult({
    fetchImpl: opts.fetchImpl,
    timeoutMs: Math.max(BRAND_LATE_TIMEOUT_MS, firstPaintMs),
  });
  let firstPaintTimer: ReturnType<typeof setTimeout> | undefined;
  const firstPaint = new Promise<null>((resolve) => {
    firstPaintTimer = setTimeout(() => resolve(null), firstPaintMs);
  });
  const result = await Promise.race([pending, firstPaint]);
  if (firstPaintTimer) clearTimeout(firstPaintTimer);

  if (result === null) {
    // 首屏超时:先回落,晚到的结果按 SWR 同口径处理。
    applyBrand(entry, fallback, opts.doc);
    void pending.then((late) => {
      const fresh = late.kind === "ok" ? late.brand : null;
      if (fresh) {
        remember(fresh);
        if (!sameBrand(fresh, getBrand())) applyBrand(entry, fresh, opts.doc);
      } else if (late.kind === "not-found") {
        markNotFound();
      }
      opts.onRefreshed?.(fresh);
    });
    return fallback;
  }
  if (result.kind === "not-found") {
    markNotFound();
    opts.onRefreshed?.(null);
    return NEUTRAL_BRAND;
  }
  const fetched = result.kind === "ok" ? result.brand : null;
  const brand = fetched ?? fallback;
  if (fetched) remember(fetched);
  applyBrand(entry, brand, opts.doc);
  opts.onRefreshed?.(fetched);
  return brand;
}
